import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Upload, Sparkles, FileImage, FileText, X,
  Trash2, Check, AlertCircle, Loader2, RotateCcw, Zap
} from 'lucide-react';
import { host } from '../script/variables';
import { uploadAndGetKeyV2 } from '../script/uploadViaPresignV2';
import '../style/MenuScan.css';

/* ─── Constants ─────────────────────────────────────────── */
const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snacks', 'dessert', 'water'];
const DISH_TYPES = ['veg', 'nonveg', 'omnivores'];

const TYPE_COLORS = {
  veg:       { bg: '#f0fdf4', text: '#16a34a', dot: '#22c55e' },
  nonveg:    { bg: '#fff1f2', text: '#e11d48', dot: '#f43f5e' },
  omnivores: { bg: '#fffbeb', text: '#d97706', dot: '#f59e0b' },
};

const normaliseDish = (raw) => {
  const typeMap = { 'non-veg': 'nonveg', veg: 'veg', nonveg: 'nonveg', omnivores: 'omnivores' };
  const sz = (s) => ({ price: s?.price ?? 0, discount: s?.discount ?? 0 });
  return {
    _id:         raw._id         || null,   // DB id — needed for the confirm update
    _key:        Math.random().toString(36).slice(2),
    name:        raw.name        || '',
    isAvailable: raw.isAvailable ?? true,
    isTaxable:   raw.isTaxable   ?? true,
    image:       raw.image       || 'null',
    type:        typeMap[raw.type] || 'veg',
    small:       sz(raw.small),
    medium:      sz(raw.medium),
    large:       sz(raw.large),
  };
};

const emptyDish = () => normaliseDish({});

/* ═══════════════════════════════════════════════════════════
   API HELPERS
═══════════════════════════════════════════════════════════ */
const extractDishes = async (token, cafeid, fileKey) => {
  const r = await fetch(`${host}/cafe/menu/scan/extract`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body:    JSON.stringify({ cafeid, fileKey }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data?.msg || `Extraction failed (${r.status})`);
  const menuBlocks = Array.isArray(data.menu) ? data.menu : [];
  if (menuBlocks.length === 0) throw new Error('No dishes found. Try a clearer image.');
  return menuBlocks;
};

/* Bulk save — strips _key before sending, keeps isTaxable */
const stripKeys = (menu) =>
  menu.map(block => ({
    ...block,
    categories: (block.categories || []).map(cat => ({
      ...cat,
      dishes: (cat.dishes || []).map(({ _key, ...dish }) => dish),
    })),
  }));

const bulkAddDishes = async (token, cafeid, menu) => {
  const r = await fetch(`${host}/cafe/menu/bulkAdd`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body:    JSON.stringify({ cafeid, menu: stripKeys(menu) }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data?.msg || 'Bulk save failed');
  return data;
};

const getPendingDishes = async (token, cafeid) => {
  const r = await fetch(`${host}/cafe/menu/scan/pending?cafeid=${cafeid}`, {
    headers: { Authorization: token },
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data?.msg || 'Failed to fetch pending dishes');
  return data;
};

/* ═══════════════════════════════════════════════════════════
   DISH ROW
═══════════════════════════════════════════════════════════ */
const DishRow = ({ dish, rowNum, onUpdate, onRemove }) => {
  const upd     = (field, val) => onUpdate(dish._key, { [field]: val });
  const updSize = (sz, val)    => onUpdate(dish._key, { [sz]: { ...dish[sz], price: Number(val) } });

  const cycleType = () => {
    const idx = DISH_TYPES.indexOf(dish.type);
    upd('type', DISH_TYPES[(idx + 1) % DISH_TYPES.length]);
  };

  const col = TYPE_COLORS[dish.type] || TYPE_COLORS.veg;

  return (
    <tr className="ms-row">
      <td className="ms-td-idx">{rowNum}</td>

      <td className="ms-td ms-td-first">
        <input className="ms-inp" value={dish.name} placeholder="Dish name…"
          onChange={e => upd('name', e.target.value)} />
      </td>

      <td className="ms-td">
        <button
          className="ms-type-tag"
          style={{ background: col.bg, color: col.text }}
          onClick={cycleType}
          title="Click to cycle veg → nonveg → omnivores"
        >
          <span className="ms-type-dot" style={{ background: col.dot }} />
          {dish.type}
        </button>
      </td>

      {['small', 'medium', 'large'].map(sz => (
        <td key={sz} className="ms-td ms-td-price">
          <input
            className="ms-inp ms-inp-price"
            type="number"
            min="0"
            value={dish[sz]?.price || ''}
            placeholder="—"
            onChange={e => updSize(sz, e.target.value)}
          />
        </td>
      ))}

      {/* ── isTaxable toggle ── */}
      <td className="ms-td ms-td-tax">
        <button
          className={`ms-tax-toggle ${dish.isTaxable ? 'ms-tax-toggle--on' : 'ms-tax-toggle--off'}`}
          onClick={() => upd('isTaxable', !dish.isTaxable)}
          title={dish.isTaxable ? 'Taxable — click to mark non-taxable' : 'Non-taxable — click to mark taxable'}
        >
          {dish.isTaxable ? 'GST' : 'No GST'}
        </button>
      </td>

      <td className="ms-td-del">
        <button className="ms-del-btn" onClick={() => onRemove(dish._key, dish._id)} title="Remove">
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN: MenuScan
═══════════════════════════════════════════════════════════ */
const MenuScan = ({ cafeid }) => {
  const token = localStorage.getItem('partnertoken');

  const [phase,      setPhase]      = useState('upload');
  const [file,       setFile]       = useState(null);
  const [preview,    setPreview]    = useState(null);
  const [uploadPct,  setUploadPct]  = useState(0);
  const [menu,       setMenu]       = useState([]);
  const [error,      setError]      = useState('');
  const [dragging,   setDragging]   = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const inputRef = useRef();

  // On mount: check DB for any unreviewed AI draft so a page refresh doesn't lose work
  useEffect(() => {
    if (!cafeid || !token) return;
    getPendingDishes(token, cafeid)
      .then(data => {
        if (!data.count) return;
        const normalisedMenu = data.menu.map(block => ({
          ...block,
          categories: (block.categories || []).map(cat => ({
            ...cat,
            dishes: (cat.dishes || []).map(normaliseDish),
          })),
        }));
        setMenu(normalisedMenu);
        setPhase('review');
      })
      .catch(() => {}); // silent — user can still upload fresh
  }, [cafeid]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = menu.reduce((n, block) =>
    n + (block.categories || []).reduce((m, cat) => m + (cat.dishes || []).length, 0), 0);

  // Count taxable dishes for the summary chip
  const taxableCount = menu.reduce((n, block) =>
    n + (block.categories || []).reduce((m, cat) =>
      m + (cat.dishes || []).filter(d => d.isTaxable).length, 0), 0);

  const acceptFile = useCallback((f) => {
    if (!f) return;
    if (!['image/jpeg','image/png','image/webp','image/heic','application/pdf'].includes(f.type)) {
      setError('Please upload a JPG, PNG, WEBP or PDF file.'); return;
    }
    if (f.size > 20 * 1024 * 1024) {
      setError('File too large. Max 20 MB.'); return;
    }
    setFile(f);
    setError('');
    setPreview(f.type !== 'application/pdf' ? URL.createObjectURL(f) : 'pdf');
  }, []);

  const onDrop      = useCallback((e) => { e.preventDefault(); setDragging(false); acceptFile(e.dataTransfer.files[0]); }, [acceptFile]);
  const onDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  const handleExtract = async () => {
    if (!file) return;
    setError('');
    try {
      setPhase('uploading'); setUploadPct(0);
      const { fileKey } = await uploadAndGetKeyV2(file, 'menu', setUploadPct, cafeid);

      setPhase('extracting');
      const raw = await extractDishes(token, cafeid, fileKey);

      const normalisedMenu = raw.map(block => ({
        ...block,
        categories: (block.categories || []).map(cat => ({
          ...cat,
          dishes: (cat.dishes || []).map(normaliseDish),  // isTaxable defaults to true here
        })),
      }));
      setMenu(normalisedMenu);
      setPhase('review');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please retry.');
      setPhase('upload');
    }
  };

  const removeDish = (_key, _id) => {
    // Optimistically remove from UI
    setMenu(prev =>
      prev.map(block => ({
        ...block,
        categories: (block.categories || []).map(cat => ({
          ...cat,
          dishes: (cat.dishes || []).filter(d => d._key !== _key),
        })).filter(cat => (cat.dishes || []).length > 0),
      })).filter(block => (block.categories || []).length > 0)
    );

    // Soft-delete in DB (isRemoved:true, isReviewed stays false)
    if (_id) {
      fetch(`${host}/cafe/menu/scan/removeDish`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body:    JSON.stringify({ cafeid, dishId: _id }),
      }).catch(err => console.error('[removeDish]', err));
    }
  };

  const updateDish = (_key, updated) => setMenu(prev =>
    prev.map(block => ({
      ...block,
      categories: (block.categories || []).map(cat => ({
        ...cat,
        dishes: (cat.dishes || []).map(d => d._key === _key ? { ...d, ...updated } : d),
      })),
    }))
  );

  // Bulk-toggle all isTaxable values at once
  const toggleAllTaxable = (val) => setMenu(prev =>
    prev.map(block => ({
      ...block,
      categories: (block.categories || []).map(cat => ({
        ...cat,
        dishes: (cat.dishes || []).map(d => ({ ...d, isTaxable: val })),
      })),
    }))
  );

  const handleBulkImport = async () => {
    if (!total) return;
    setBulkSaving(true);
    setError('');
    try {
      await bulkAddDishes(token, cafeid, menu);
      setPhase('done');
    } catch (err) {
      setError(err.message || 'Import failed. Please retry.');
    } finally {
      setBulkSaving(false);
    }
  };

  const reset = () => {
    setPhase('upload'); setFile(null); setPreview(null);
    setMenu([]); setError(''); setUploadPct(0); setBulkSaving(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  /* ══════════════════════════════════════════════════════════
     RENDER: UPLOAD
  ══════════════════════════════════════════════════════════ */
  if (phase === 'upload') return (
    <div className="ms-root">
      <div className="ms-upload-wrap">
        <div className="ms-badge"><Sparkles size={11} /><span>AI-Powered</span></div>
        <h2 className="ms-hero-title">MenuScan</h2>
        <p className="ms-hero-sub">Upload a photo or PDF of your menu — every dish extracted automatically.</p>

        <div
          className={`ms-dropzone ${dragging ? 'drag' : ''} ${file ? 'ready' : ''}`}
          onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
          onClick={() => !file && inputRef.current?.click()}
        >
          <input ref={inputRef} type="file" accept="image/*,.pdf"
            style={{ display: 'none' }}
            onChange={e => acceptFile(e.target.files[0])} />

          {!file ? (
            <>
              <div className="ms-dz-icon"><Upload size={22} /></div>
              <p className="ms-dz-main">Drop your menu here</p>
              <p className="ms-dz-hint">JPG · PNG · WEBP · PDF &nbsp;·&nbsp; max 20 MB</p>
              <div className="ms-dz-pills">
                <span className="ms-dz-pill"><FileImage size={10} /> Photo</span>
                <span className="ms-dz-pill"><FileText size={10} /> PDF</span>
                <span className="ms-dz-pill"><FileImage size={10} /> Screenshot</span>
              </div>
            </>
          ) : (
            <>
              {preview !== 'pdf'
                ? <img src={preview} alt="Menu preview" className="ms-preview-img" />
                : <div className="ms-dz-icon" style={{ margin: '0 auto 12px' }}><FileText size={22} /></div>
              }
              <div className="ms-file-meta">
                <span className="ms-file-name">{file.name}</span>
                <span className="ms-file-size">{(file.size / 1024).toFixed(0)} KB</span>
              </div>
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <button className="ms-file-clear" onClick={e => { e.stopPropagation(); reset(); }}>
                  <X size={11} /> Change file
                </button>
              </div>
            </>
          )}
        </div>

        {error && <div className="ms-error-box"><AlertCircle size={14} /><span>{error}</span></div>}

        <button className="ms-extract-btn" onClick={handleExtract} disabled={!file}>
          <Sparkles size={15} /> Extract Dishes from Menu
        </button>

        <div className="ms-tips">
          <p className="ms-tips-title">Tips for best results</p>
          <ul>
            <li>Use a well-lit, flat photo — avoid steep angles</li>
            <li>All text should be in focus and readable</li>
            <li>PDFs work great — even scanned ones</li>
            <li>Works with Hindi / regional language menus too</li>
          </ul>
        </div>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     RENDER: UPLOADING
  ══════════════════════════════════════════════════════════ */
  if (phase === 'uploading') return (
    <div className="ms-root ms-center">
      <div className="ms-progress-card">
        <div className="ms-phase-icon"><Upload size={22} /></div>
        <h3 className="ms-phase-title">Uploading menu…</h3>
        <div className="ms-prog-bar-wrap">
          <div className="ms-prog-bar" style={{ width: `${uploadPct}%` }} />
        </div>
        <p className="ms-prog-label">{uploadPct}%</p>
        <p className="ms-phase-hint">Sending to secure cloud storage</p>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     RENDER: EXTRACTING
  ══════════════════════════════════════════════════════════ */
  if (phase === 'extracting') return (
    <div className="ms-root ms-center">
      <div className="ms-progress-card">
        {preview && preview !== 'pdf' ? (
          <div className="ms-scan-wrap">
            <div className="ms-scan-line" />
            <img src={preview} alt="" className="ms-scan-img" />
          </div>
        ) : (
          <div className="ms-phase-icon"><FileText size={22} /></div>
        )}
        <div className="ms-extract-status">
          <Loader2 size={15} className="ms-spin" style={{ color: '#f5c842' }} />
          <span>AI is reading your menu…</span>
        </div>
        <p className="ms-phase-hint">Identifying dishes, sizes and prices</p>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     RENDER: REVIEW TABLE
  ══════════════════════════════════════════════════════════ */
  if (phase === 'review') return (
    <div className="ms-root ms-review-shell">

      <div className="ms-review-header">
        <div>
          <div className="ms-review-title-row">
            <h2 className="ms-review-title">Review Dishes</h2>
            <span className="ms-count-badge">{total}</span>
          </div>
          <p className="ms-review-sub">Review and edit, then import everything in one click.</p>
        </div>
        <div className="ms-header-actions">
          <button className="ms-ghost-btn" onClick={reset}><RotateCcw size={12} /> Start Over</button>
          <button className="ms-import-btn" onClick={handleBulkImport} disabled={bulkSaving || !total}>
            {bulkSaving
              ? <><Loader2 size={13} className="ms-spin" /> Saving…</>
              : <><Zap size={13} /> Import {total} Dishes</>
            }
          </button>
        </div>
      </div>

      {error && <div className="ms-error-box" style={{ margin: '12px 28px 0' }}><AlertCircle size={14} /><span>{error}</span></div>}

      {/* ── Tax bulk-toggle bar ── */}
      <div className="ms-tax-bar">
        <span className="ms-tax-bar-label">
          GST applied to <strong>{taxableCount}</strong> of {total} dishes
        </span>
        <div className="ms-tax-bar-actions">
          <button className="ms-tax-bar-btn ms-tax-bar-btn--on"  onClick={() => toggleAllTaxable(true)}>
            Mark all taxable
          </button>
          <button className="ms-tax-bar-btn ms-tax-bar-btn--off" onClick={() => toggleAllTaxable(false)}>
            Mark all non-taxable
          </button>
        </div>
      </div>

      <div className="ms-table-wrap">
        <table className="ms-table">
          <thead>
            <tr>
              <th className="ms-th" style={{ width: 36 }}>#</th>
              <th className="ms-th">Dish Name</th>
              <th className="ms-th">Type</th>
              <th className="ms-th ms-th-price">S ₹</th>
              <th className="ms-th ms-th-price">M ₹</th>
              <th className="ms-th ms-th-price">L ₹</th>
              <th className="ms-th ms-th-tax">GST</th>
              <th className="ms-th" style={{ width: 44 }}></th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              let rowNum = 0;
              return menu.map((block, bi) =>
                (block.categories || []).map((cat, ci) => [
                  <tr key={`cat-${bi}-${ci}`}>
                    <td colSpan={8} className="ms-td-cat-header">
                      {cat.name}
                      <span className="ms-cat-count">{(cat.dishes || []).length}</span>
                    </td>
                  </tr>,
                  ...(cat.dishes || []).map((dish) => {
                    rowNum += 1;
                    return (
                      <DishRow
                        key={dish._key}
                        dish={dish}
                        rowNum={rowNum}
                        onUpdate={updateDish}
                        onRemove={removeDish}
                      />
                    );
                  }),
                ])
              );
            })()}
          </tbody>
        </table>
      </div>

      <div className="ms-footer">
        <div className="ms-footer-stats">
          <span className="ms-stat-chip">{total} dishes</span>
          <span className="ms-stat-chip ms-stat-chip--tax">{taxableCount} taxable</span>
        </div>
        <button className="ms-import-btn" onClick={handleBulkImport} disabled={bulkSaving || !total}>
          {bulkSaving
            ? <><Loader2 size={13} className="ms-spin" /> Saving all dishes…</>
            : <><Zap size={13} /> Import All {total} Dishes</>
          }
        </button>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     RENDER: DONE
  ══════════════════════════════════════════════════════════ */
  if (phase === 'done') return (
    <div className="ms-root ms-center">
      <div className="ms-done-card">
        <div className="ms-done-icon"><Check size={28} /></div>
        <h3 className="ms-done-title">Import Complete!</h3>
        <p className="ms-done-count">{total} dishes added to your menu</p>
        <button className="ms-rescan-btn" onClick={reset}>
          <RotateCcw size={13} /> Scan Another Menu
        </button>
      </div>
    </div>
  );

  return null;
};

export default MenuScan;